"use client";

// Plans v2 — paleta crema con cards blancas. Replica el pricing del landing.
// Conserva la lógica del v1: monthly/annual toggle, planes desde DB,
// metadata estática (badge "Más popular", descripción, features) por plan.

import Link from "next/link";
import type { PublicPlanConfig } from "@/app/actions/public-plan-configs";
import { PublicShell } from "@/components/brand/PublicShell";

const PLAN_META: Record<string, {
  name: string;
  description: string;
  badge?: string;
  highlight: boolean;
  features: string[];
}> = {
  lite: {
    name: "Lite",
    description: "Para freelancers o equipos chicos con una búsqueda activa.",
    highlight: false,
    features: [
      "Portal de empleo con tu marca",
      "Análisis de CVs con IA",
      "Pipeline kanban",
      "Plantillas de email",
      "Soporte por email",
    ],
  },
  starter: {
    name: "Starter",
    description: "Para equipos que recién arrancan a sistematizar reclutamiento.",
    highlight: false,
    features: [
      "Todo lo de Lite",
      "Más búsquedas y usuarios en paralelo",
      "Análisis IA avanzado",
      "Plantillas de email ilimitadas",
      "Soporte prioritario",
    ],
  },
  growth: {
    name: "Growth",
    description: "Para equipos con múltiples búsquedas abiertas en paralelo.",
    badge: "MÁS ELEGIDO",
    highlight: true,
    features: [
      "Todo lo de Starter",
      "Pool de talentos",
      "Scorecards con peso por criterio",
      "Analytics: funnel, time-to-hire, fuente",
      "Emails masivos + recordatorios automáticos",
      "Gestión de equipo y permisos",
      "Soporte prioritario",
    ],
  },
};

// Helpers de formato — siguen el mismo copy que la sección #planes de la landing.
const fmtJobs = (val: number | null) => val === null ? "Ilimitadas" : String(val);
const fmtUsers = (val: number | null) => val === null ? "Ilimitados" : String(val);

export default function PlansClient({
  configs,
  locale,
  loggedIn = false,
  trialDays = 7,
}: {
  configs: PublicPlanConfig[];
  locale: string;
  loggedIn?: boolean;
  /** Días de trial configurados por el superadmin (platform_config.default_trial_days).
   *  Se inyectan en el CTA "Empezar X días gratis" para que coincida con la landing. */
  trialDays?: number;
}) {
  // Estado `annual` removido (Talenv solo cobra mensual).

  return (
    <PublicShell locale={locale} card={false} maxWidth={1000}>
      {/* OJO con FOUC: TODOS los layout-styles que afectan el primer paint
          (text-align, grid-template-columns, padding, margin) se aplican via
          inline style en el JSX para que el HTML renderizado en SSR ya tenga
          el aspecto final desde el primer pintado. Si los ponemos en
          styled-jsx, recien se aplican tras hidratacion → flicker visible
          (titulo arrancaba alineado a la izquierda, cards apiladas, etc.).
          Las clases .page-title / .page-lead vienen de globals.css (cargado
          en el <head>) asi que esas SI estan disponibles desde el primer
          paint y no requieren tratamiento especial. Solo dejamos en styled-jsx
          el media query mobile que inline no puede expresar. */}
      <style jsx>{`
        @media (max-width: 880px) {
          .grid {
            grid-template-columns: 1fr !important;
            max-width: 460px;
            margin-left: auto !important;
            margin-right: auto !important;
          }
        }
      `}</style>

      <div style={{ textAlign: "center", padding: "24px 16px 32px" }}>
        <h1 className="page-title">Elegí tu plan</h1>
        <p className="page-lead">
          Todos los planes incluyen análisis de CV con IA y portal de empleo con tu marca.
        </p>
      </div>

      {configs.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "40px 24px",
          background: "white", border: "1px solid rgba(21,16,61,0.08)", borderRadius: 16,
          color: "#6B6982", fontSize: 14, maxWidth: 480, margin: "0 auto",
        }}>
          No hay planes configurados todavía.
          <br />Escribinos a <a href="mailto:hola@talenv.com" style={{ color: "#6C5CFF", textDecoration: "none" }}>hola@talenv.com</a>
        </div>
      ) : (
        <div
          className="grid"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${configs.length || 2}, 1fr)`,
            gap: 18,
            marginTop: 28,
          }}
        >
          {configs.map((c) => (
            <PlanCard key={c.plan} config={c} locale={locale} loggedIn={loggedIn} trialDays={trialDays} />
          ))}
        </div>
      )}

      <p style={{ textAlign: "center", fontSize: 12, color: "#A19FB6", marginTop: 32 }}>
        Todos los precios en USD. Pagos procesados de forma segura con dLocal Go.
      </p>
    </PublicShell>
  );
}

function PlanCard({
  config, locale, loggedIn, trialDays,
}: {
  config: PublicPlanConfig; locale: string; loggedIn: boolean; trialDays: number;
}) {
  const meta = PLAN_META[config.plan];
  if (!meta) return null;

  const displayPrice = config.price_monthly_usd;
  const isHl = meta.highlight;

  return (
    <div
      style={{
        position: "relative",
        background: isHl ? "#15103D" : "white",
        color: isHl ? "white" : "#15103D",
        border: isHl ? "none" : "1px solid rgba(21,16,61,0.08)",
        borderRadius: 18,
        padding: "32px 32px 30px",
        boxShadow: isHl
          ? "0 30px 60px -20px rgba(21,16,61,0.4)"
          : "0 1px 0 rgba(255,255,255,0.7) inset, 0 14px 30px -16px rgba(21,16,61,0.16)",
        display: "flex", flexDirection: "column",
      }}
    >
      {meta.badge && (
        <div style={{
          position: "absolute", top: -12, right: 24,
          padding: "5px 12px", borderRadius: 999,
          background: "#D9FF5B", color: "#15103D",
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 11, fontWeight: 500,
          textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          {meta.badge}
        </div>
      )}

      {/* OJO: PublicShell aplica estilos globales a h3 que pisan el `color`
          heredado del div padre, asi que la card Growth (fondo oscuro)
          quedaba con el titulo "Growth" en negro e ilegible. Forzamos el
          color explicito por inline-style para que tenga precedencia. */}
      <h3 style={{
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        fontWeight: 700, fontSize: 26, letterSpacing: "-0.02em",
        margin: "0 0 8px",
        color: isHl ? "white" : "#15103D",
      }}>
        {meta.name}
      </h3>
      <p style={{
        fontSize: 14.5, lineHeight: 1.45, marginBottom: 24,
        color: isHl ? "rgba(255,255,255,0.65)" : "#6B6982",
        minHeight: 42,
      }}>
        {meta.description}
      </p>

      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
        <span style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 16, fontWeight: 500,
          color: isHl ? "rgba(255,255,255,0.6)" : "#6B6982",
        }}>USD</span>
        <span style={{
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          fontWeight: 700, fontSize: 56, letterSpacing: "-0.03em", lineHeight: 1,
        }}>
          {displayPrice}
        </span>
        <span style={{ fontSize: 14.5, color: isHl ? "rgba(255,255,255,0.6)" : "#6B6982" }}>/ mes</span>
      </div>

      {/* Meta de límites — formato compacto idéntico a la sección #planes de
          la landing: "Hasta X búsquedas · Y usuarios". El campo `max_emails`
          existe en plan_configs pero no se muestra acá (la landing tampoco
          lo expone — es info técnica que no aporta a la decisión de compra). */}
      <div style={{
        fontSize: 13.5,
        color: isHl ? "rgba(255,255,255,0.7)" : "#6B6982",
        paddingBottom: 22, marginBottom: 22,
        borderBottom: `1px solid ${isHl ? "rgba(255,255,255,0.10)" : "rgba(21,16,61,0.08)"}`,
      }}>
        Hasta <b style={{ color: isHl ? "white" : "#15103D" }}>{fmtJobs(config.max_jobs)} búsquedas</b>
        {" · "}
        <b style={{ color: isHl ? "white" : "#15103D" }}>{fmtUsers(config.max_users)} usuarios</b>
      </div>

      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: 12 }}>
        {meta.features.map((f) => (
          <li key={f} style={{
            display: "flex", gap: 10, alignItems: "flex-start",
            fontSize: 14.5, lineHeight: 1.4,
            color: isHl ? "rgba(255,255,255,0.85)" : "#2A2553",
          }}>
            <span style={{
              flexShrink: 0, marginTop: 5,
              width: 14, height: 14, borderRadius: "50%",
              background: isHl ? "rgba(217,255,91,0.18)" : "#ECE8FF",
              backgroundImage: isHl
                ? "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='%23D9FF5B' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round' d='M3.5 8.5l3 3 6-7'/></svg>\")"
                : "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='%236C5CFF' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round' d='M3.5 8.5l3 3 6-7'/></svg>\")",
              backgroundSize: "12px 12px",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }} />
            {f}
          </li>
        ))}
      </ul>

      {/* CTA dinámico: si el user ya está logueado (típicamente trial
          expirado o cancelado mirando /plans desde el aviso), lo mandamos
          a /activate (paywall con MP). Antes el CTA siempre iba a
          /register → con session activa, el middleware lo redirigía a
          /account-suspended → CTA "Activar mi plan" → /plans... loop. */}
      <Link
        href={loggedIn ? `/${locale}/activate` : `/${locale}/register?plan=${config.plan}`}
        style={{
          marginTop: "auto",
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
          padding: "12px 18px", borderRadius: 11,
          background: isHl ? "#6C5CFF" : "white",
          color: isHl ? "white" : "#15103D",
          border: isHl ? "none" : "1px solid rgba(21,16,61,0.14)",
          fontSize: 14.5, fontWeight: 600, textDecoration: "none",
          boxShadow: isHl
            ? "0 1px 0 rgba(255,255,255,0.2) inset, 0 8px 22px -10px rgba(108,92,255,0.7)"
            : "none",
          transition: "background .15s, transform .12s, border-color .15s",
        }}
      >
        {loggedIn
          ? "Activar este plan"
          : trialDays > 0
            ? `Empezar ${trialDays} días gratis`
            : "Suscribirme"}
      </Link>
    </div>
  );
}

/* Helper `Limit` removido — antes mostraba 3 filas enmarcadas (búsquedas/
   usuarios/emails). La sección #planes de la landing usa una sola línea
   compacta "Hasta X búsquedas · Y usuarios" y ese es el diseño canónico que
   /plans replica. El campo max_emails sigue en la DB pero no se muestra. */
